import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Box, FormHelperText, FormControl, Typography, FormLabel } from "@mui/material";

import { CreateContractData } from "../../../types/service.type";
import { CurrencyEnum, ServiceRequestTypeEnum } from "../../../enum/service.enum";

import { currencyList } from "../../../../../enums/currencies.enum";
import TextFieldWithSelectMenu from "../../../../../shared/Form/TextFieldWithSelectMenu";
import { useMinimumServicePrices } from "../../../../../api/queries/payment/paymentQueries";

const ServicePriceFields = ({
  serviceCurrency,
  isDataLoading,
  serviceType,
}: {
  serviceCurrency: CurrencyEnum;
  isDataLoading: boolean;
  serviceType: ServiceRequestTypeEnum;
}) => {
  const { t } = useTranslation("goService", { keyPrefix: "createContractPage" });
  const {
    control,
    formState: { errors, isSubmitted, touchedFields },
    watch,
    setValue,
  } = useFormContext<CreateContractData>();

  const amount = watch("paymentConfig.amount");
  const currency = watch("paymentConfig.currency");

  const type = watch("paymentConfig.type");
  const unit = watch("paymentConfig.unit");
  const milestones = watch("milestones");
  const milestonesCount = milestones?.length;

  const totalPrice = amount ? amount * (milestonesCount ? milestonesCount : 1) : 0;

  const shouldShowMilestoneError = isSubmitted || touchedFields.milestones;
  const hasMilestoneError = milestonesCount === 0 || milestonesCount > 20;

  const { minimumServicePrices } = useMinimumServicePrices();
  const minimumServicePrice =
    currency && minimumServicePrices ? minimumServicePrices[currency as keyof typeof minimumServicePrices] : undefined;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t("price")}</Typography>
      <Box sx={styles.priceContainer}>
        <FormControl
          fullWidth
          component="fieldset"
          sx={styles.formControl}
          error={!!errors.paymentConfig?.amount || !!errors.paymentConfig?.currency}
        >
          <FormLabel sx={styles.formLabel}>
            {serviceType === ServiceRequestTypeEnum.Unit ? t("pricePerServiceUnit") : t("servicePrice")}
          </FormLabel>
          <Controller
            name="paymentConfig.currency"
            control={control}
            render={({ field: currencyField }) => (
              <Controller
                name="paymentConfig.amount"
                control={control}
                render={({ field: amountField }) => (
                  <TextFieldWithSelectMenu
                    options={currencyList.map((currency) => ({ label: currency, value: currency }))}
                    selectValue={isDataLoading ? "" : serviceCurrency}
                    textFieldPlaceholder={t("enterPricePerUnit")}
                    textFieldValue={String(amountField.value || "")}
                    onSelectChange={currencyField.onChange}
                    onTextFieldChange={amountField.onChange}
                  />
                )}
              />
            )}
          />
          <FormHelperText error={!!errors.paymentConfig?.amount}>
            {errors.paymentConfig?.amount?.message
              ? t(errors.paymentConfig.amount.message, { amount: minimumServicePrice })
              : t("thisPriceIncludeGoalngoFeesAndThisFeesIsNotRefundable")}
          </FormHelperText>
        </FormControl>
        {type === ServiceRequestTypeEnum.Unit && (
          <FormControl
            fullWidth
            component="fieldset"
            sx={styles.formControl}
            error={shouldShowMilestoneError && hasMilestoneError}
          >
            <FormLabel sx={styles.formLabel}>{t("numberOfServiceUnit")}</FormLabel>
            <TextFieldWithSelectMenu
              options={unit ? [{ label: t(unit), value: unit }] : []}
              selectValue={isDataLoading ? "" : unit || ""}
              textFieldPlaceholder={t("enterNumberOfServices")}
              textFieldValue={milestonesCount ? String(milestonesCount) : ""}
              onSelectChange={() => {}}
              onTextFieldChange={(value) => {
                const numValue = parseInt(value) || 0;
                if (numValue > 0 && numValue <= 20) {
                  const currentMilestones = milestones || [];
                  const currentLength = currentMilestones.length;

                  if (numValue > currentLength) {
                    // Add more milestones
                    const newMilestones = [...currentMilestones];
                    for (let i = currentLength; i < numValue; i++) {
                      newMilestones.push({
                        name: "",
                        deliveryDueDate: "",
                        paymentDueDate: "",
                      });
                    }
                    setValue("milestones", newMilestones, { shouldTouch: true });
                  } else if (numValue < currentLength) {
                    // Remove milestones
                    setValue("milestones", currentMilestones.slice(0, numValue), { shouldTouch: true });
                  }
                } else if (value === "") {
                  setValue("milestones", [], { shouldTouch: true });
                }
              }}
            />
            {shouldShowMilestoneError && hasMilestoneError && (
              <FormHelperText error>
                {milestonesCount === 0
                  ? "At least 1 milestone is required"
                  : milestonesCount > 20
                  ? "Maximum 20 milestones allowed"
                  : ""}
              </FormHelperText>
            )}
          </FormControl>
        )}
        <Box sx={styles.totalContractPriceContainer}>
          <Typography sx={styles.totalContractPrice}>{t("totalContractPrice")}</Typography>
          <Typography sx={styles.totalContractPriceValue}>
            {totalPrice} {serviceCurrency}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ServicePriceFields;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  title: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
  priceContainer: {
    width: "100%",
    display: "flex",
    paddingTop: "16px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "24px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--Base-White, #FFF)",
  },
  formControl: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    px: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111) !important",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  totalContractPriceContainer: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px",
    borderRadius: "0px 0px 12px 12px",
    border: "1px solid #EAECF0",
    background: "var(--Brand-25, #F6FFF5)",
  },
  totalContractPrice: {
    fontFamily: "Montserrat",
    color: "var(--Brand-700, #076D0D)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
  totalContractPriceValue: {
    fontFamily: "Montserrat",
    color: "var(--Brand-700, #076D0D)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "20px",
  },
};
